/**
 * PlatiTab.jsx - Sistem de Credite/Plăți
 * 1 credit = 1 RON
 * Funcționalități: vizualizare sold, adăugare credite, istoric tranzacții, setare prețuri acțiuni
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Coins, Plus, History, Settings, ArrowUpCircle, ArrowDownCircle, 
  Loader2, AlertTriangle, Building2, RefreshCw, Save, Edit3, Check,
  Send, ClipboardList, CheckCircle, XCircle, CircleDollarSign, Eye, FileText, CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlatiTab({ companyId, userData, onBalanceChange }) {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [actionPrices, setActionPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  
  // Dialog pentru adăugare credite
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [addDescription, setAddDescription] = useState('Alimentare cont');
  const [addingCredits, setAddingCredits] = useState(false);
  
  // Super admin - lista companii
  const [companiesCredits, setCompaniesCredits] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyForAdd, setSelectedCompanyForAdd] = useState(null);
  
  // Editare prețuri
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState('');

  // Credit Requests
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Financiar (proforme + facturi e-Tesa)
  const [financialDocs, setFinancialDocs] = useState([]);
  const [financialStatus, setFinancialStatus] = useState({ pending_proformas: [], credits_blocked: false, blocking_proforma: null });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [payingOnline, setPayingOnline] = useState(false);
  
  const isSuperAdmin = userData?.role === 'superadmin';
  const isAdmin = userData?.role === 'master' || userData?.role === 'admin';

  // Fetch sold credite
  const fetchBalance = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/credits/balance`, { params: { company_id: companyId } });
      setBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Eroare la încărcarea soldului:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Fetch istoric tranzacții
  const fetchTransactions = useCallback(async () => {
    if (!companyId) return;
    setLoadingTransactions(true);
    try {
      const response = await axios.get(`${API}/credits/transactions`, { params: { company_id: companyId, limit: 100 } });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Eroare la încărcarea tranzacțiilor:', error);
    } finally {
      setLoadingTransactions(false);
    }
  }, [companyId]);

  // Fetch prețuri acțiuni
  const fetchPrices = useCallback(async () => {
    setLoadingPrices(true);
    try {
      const response = await axios.get(`${API}/credits/prices`);
      setActionPrices(response.data || []);
    } catch (error) {
      console.error('Eroare la încărcarea prețurilor:', error);
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  // Fetch companii pentru super admin
  const fetchCompaniesCredits = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${API}/credits/all-companies`);
      setCompaniesCredits(response.data || []);
    } catch (error) {
      console.error('Eroare la încărcarea companiilor:', error);
    } finally {
      setLoadingCompanies(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
    fetchPrices();
    if (isSuperAdmin) {
      fetchCompaniesCredits();
    }
  }, [fetchBalance, fetchTransactions, fetchPrices, fetchCompaniesCredits, isSuperAdmin]);

  // Adăugare credite
  const handleAddCredits = async () => {
    const amount = parseFloat(addAmount);
    if (!amount || amount <= 0) {
      toast.error('Introduceți o sumă validă');
      return;
    }
    
    // Verificare limită pentru admin non-super (plafon temporar până la integrare plată)
    if (!isSuperAdmin && !selectedCompanyForAdd) {
      if (balance + amount > 500) {
        toast.error(`Puteți adăuga maxim ${Math.max(0, 500 - balance)} credite (limită 500)`);
        return;
      }
    }
    
    setAddingCredits(true);
    try {
      const targetCompanyId = selectedCompanyForAdd?.company_id || companyId;
      
      if (isSuperAdmin && selectedCompanyForAdd) {
        // Super admin - fără limită
        await axios.post(`${API}/credits/admin-add`, null, {
          params: { company_id: targetCompanyId, amount, description: addDescription }
        });
      } else {
        // Admin - cu limită
        await axios.post(`${API}/credits/add`, {
          company_id: targetCompanyId,
          amount,
          description: addDescription
        }, { params: { is_super_admin: isSuperAdmin } });
      }
      
      toast.success(`S-au adăugat ${amount} credite`);
      setShowAddCredits(false);
      setAddAmount('');
      setAddDescription('Alimentare cont');
      setSelectedCompanyForAdd(null);
      fetchBalance();
      fetchTransactions();
      if (isSuperAdmin) fetchCompaniesCredits();
      // Notify parent to update balance
      onBalanceChange && onBalanceChange();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Eroare la adăugarea creditelor';
      toast.error(typeof msg === 'string' ? msg : msg.message || 'Eroare');
    } finally {
      setAddingCredits(false);
    }
  };

  // Actualizare preț acțiune
  const handleUpdatePrice = async (actionCode) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Preț invalid');
      return;
    }
    
    try {
      await axios.put(`${API}/credits/prices`, {
        action_code: actionCode,
        price: price
      });
      toast.success('Preț actualizat');
      setEditingPrice(null);
      setNewPrice('');
      fetchPrices();
    } catch (error) {
      toast.error('Eroare la actualizarea prețului');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Credit Requests handlers
  const fetchMyRequests = useCallback(async () => {
    if (!companyId || isSuperAdmin) return;
    try {
      const r = await axios.get(`${API}/credits/requests`, { params: { company_id: companyId } });
      setMyRequests(r.data || []);
    } catch { /* ignore */ }
  }, [companyId, isSuperAdmin]);

  const fetchFinancial = useCallback(async () => {
    if (!companyId || isSuperAdmin) return;
    try {
      const [docs, status] = await Promise.all([
        axios.get(`${API}/credits/financial-documents`, { params: { company_id: companyId } }),
        axios.get(`${API}/credits/financial-status`, { params: { company_id: companyId } }),
      ]);
      setFinancialDocs(docs.data || []);
      setFinancialStatus(status.data || { pending_proformas: [], credits_blocked: false });
    } catch { /* ignore */ }
  }, [companyId, isSuperAdmin]);

  const fetchAllRequests = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoadingRequests(true);
    try {
      const r = await axios.get(`${API}/superadmin/credit-requests`);
      setAllRequests(r.data || []);
    } catch { /* ignore */ } finally { setLoadingRequests(false); }
  }, [isSuperAdmin]);

  const fetchPendingCount = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const r = await axios.get(`${API}/superadmin/credit-requests/count-pending`);
      setPendingCount(r.data?.pending || 0);
    } catch { /* ignore */ }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchMyRequests();
    fetchAllRequests();
    fetchPendingCount();
    fetchFinancial();
  }, [fetchMyRequests, fetchAllRequests, fetchPendingCount, fetchFinancial]);

  const handleSubmitRequest = async () => {
    const amount = parseFloat(requestAmount);
    if (!amount || amount <= 0) { toast.error('Sumă invalidă'); return; }
    if (amount > 500) { toast.error('Sumă maximă per cerere: 500 credite'); return; }
    if (balance + amount > 500) {
      const remaining = Math.max(0, 500 - Math.floor(balance));
      toast.error(`Soldul total nu poate depăși 500 credite. Sold curent: ${balance}. Mai poți cere maxim ${remaining}.`);
      return;
    }
    setSubmittingRequest(true);
    try {
      const r = await axios.post(`${API}/credits/request`, {
        company_id: companyId, amount, notes: requestNotes
      });
      toast.success(r.data.message || 'Credite alocate + proformă emisă', { duration: 9000 });
      setShowRequestModal(false);
      setRequestAmount('');
      setRequestNotes('');
      fetchBalance();
      fetchMyRequests();
      fetchTransactions();
      fetchFinancial();
      if (onBalanceChange) onBalanceChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la trimiterea cererii');
    } finally { setSubmittingRequest(false); }
  };

  const handleApproveRequest = async (id) => {
    if (!window.confirm('Aprobi cererea? Se va genera o factură de la firma proprietară (CUI 44303561) către client.')) return;
    try {
      const r = await axios.post(`${API}/superadmin/credit-requests/${id}/approve`);
      toast.success(r.data.message || 'Cerere aprobată');
      fetchAllRequests(); fetchPendingCount();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare aprobare'); }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm('Confirmi plata? Se vor aloca creditele rămase clientului și factura va deveni „Plătită".')) return;
    try {
      const r = await axios.post(`${API}/superadmin/credit-requests/${id}/mark-paid`);
      toast.success(r.data.message || 'Plată confirmată');
      fetchAllRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare confirmare plată'); }
  };

  const handleRejectRequest = async (id) => {
    const reason = window.prompt('Motiv respingere (opțional):') || '';
    try {
      await axios.post(`${API}/superadmin/credit-requests/${id}/reject`, { reason });
      toast.success('Cerere respinsă');
      fetchAllRequests(); fetchPendingCount();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare respingere'); }
  };

  const requestStatusBadge = (s) => {
    const cfg = {
      pending:   { color: 'bg-amber-100 text-amber-700', label: 'În așteptare' },
      approved:  { color: 'bg-blue-100 text-blue-700', label: 'Aprobată — factură emisă' },
      paid:      { color: 'bg-green-100 text-green-700', label: 'Plătită — credite alocate' },
      rejected:  { color: 'bg-red-100 text-red-700', label: 'Respinsă' },
    }[s] || { color: 'bg-slate-100 text-slate-700', label: s };
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  const handlePayOnline = async (proformaId) => {
    if (!window.confirm(
      "⚠ MOD DEMO — Plata online este SIMULATĂ în perioada de probe.\n\n" +
      "Vei primi un document numit 'factură fiscală' DAR este marcat DEMO/NEFISCAL și NU trebuie folosit în contabilitate.\n\n" +
      "Confirmi procesarea simulată?"
    )) return;
    setPayingOnline(true);
    try {
      const r = await axios.post(`${API}/credits/proformas/${proformaId}/pay-online`, null, {
        params: { company_id: companyId }
      });
      toast.success(r.data?.message || "Plată procesată — factură fiscală emisă", { duration: 8000 });
      setPreviewDoc(null);
      fetchFinancial();
      fetchMyRequests();
      fetchTransactions();
      fetchBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la procesarea plății");
    } finally {
      setPayingOnline(false);
    }
  };

  return (
    <Card className="min-h-[600px]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-500" />
              Sistem Credite
            </CardTitle>
            <CardDescription>1 credit = 1 RON • Gestionare credite și plăți</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => { fetchBalance(); fetchTransactions(); fetchMyRequests(); fetchAllRequests(); fetchPendingCount(); }}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualizează
            </Button>
            {!isSuperAdmin && (
              <Button onClick={() => setShowRequestModal(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="request-credits-btn">
                <Send className="w-4 h-4 mr-2" />
                Solicită Credite
              </Button>
            )}
            {isSuperAdmin && (
              <Button onClick={() => setShowAddCredits(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-credits-btn">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Credite
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Sold Curent */}
        <div className="mb-6 p-6 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-white">
          <p className="text-sm opacity-80 mb-1">Sold Curent</p>
          {loading ? (
            <Loader2 className="w-8 h-8 animate-spin" />
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold">{balance.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className="text-xl">credite</span>
            </div>
          )}
          <p className="text-xs opacity-70 mt-2">≈ {balance.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON</p>
        </div>

        <Tabs defaultValue={financialStatus.credits_blocked || (financialStatus.pending_proformas?.length > 0) ? "financial" : "transactions"} className="w-full">
          <TabsList className={`grid w-full ${isSuperAdmin ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'} h-auto gap-1`}>
            <TabsTrigger value="transactions" className="text-xs sm:text-sm py-2">
              <History className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Istoric</span>
              <span className="sm:hidden ml-1">Istoric</span>
            </TabsTrigger>
            <TabsTrigger value="prices" className="text-xs sm:text-sm py-2">
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Prețuri Acțiuni</span>
              <span className="sm:hidden ml-1">Prețuri</span>
            </TabsTrigger>
            {!isSuperAdmin && (
              <TabsTrigger value="financial" className="text-xs sm:text-sm py-2 relative" data-testid="financial-tab">
                <CircleDollarSign className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Financiar</span>
                <span className="sm:hidden ml-1">Financiar</span>
                {financialStatus.pending_proformas?.length > 0 && (
                  <Badge className={`ml-1 text-white text-[10px] px-1.5 py-0 ${financialStatus.credits_blocked ? 'bg-red-600 animate-pulse' : 'bg-amber-500'}`}>
                    {financialStatus.pending_proformas.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            {!isSuperAdmin && (
              <TabsTrigger value="my-requests" data-testid="my-requests-tab" className="text-xs sm:text-sm py-2">
                <ClipboardList className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Cererile Mele</span>
                <span className="sm:hidden ml-1">Cereri</span>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="all-requests" data-testid="all-requests-tab" className="text-xs sm:text-sm py-2">
                <ClipboardList className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Cereri Credite</span>
                <span className="sm:hidden ml-1">Cereri</span>
                {pendingCount > 0 && (
                  <Badge className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0" data-testid="pending-requests-badge">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="companies" className="text-xs sm:text-sm py-2">
                <Building2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Atribuire Credite</span>
                <span className="sm:hidden ml-1">Atribuie</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Tab Istoric Tranzacții */}
          <TabsContent value="transactions">
            <ScrollArea className="h-[400px]">
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nu există tranzacții</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((t) => (
                    <div key={t.id || t.created_at} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        {t.type === 'credit' ? (
                          <ArrowUpCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <ArrowDownCircle className="w-5 h-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{t.description}</p>
                          <p className="text-xs text-slate-500">{formatDate(t.created_at)}</p>
                        </div>
                      </div>
                      <Badge variant={t.type === 'credit' ? 'success' : 'destructive'} className={t.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {t.type === 'credit' ? '+' : '-'}{t.amount} cr
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Tab Prețuri Acțiuni */}
          <TabsContent value="prices">
            <div className="space-y-2">
              {loadingPrices ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                actionPrices.map((action) => (
                  <div key={action.action_code} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{action.name}</p>
                      <p className="text-xs text-slate-500">{action.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingPrice === action.action_code ? (
                        <>
                          <Input 
                            type="number"
                            value={newPrice}
                            onChange={(e) => setNewPrice(e.target.value)}
                            className="w-20 h-8"
                            placeholder="0"
                          />
                          <Button size="sm" variant="ghost" onClick={() => handleUpdatePrice(action.action_code)}>
                            <Check className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingPrice(null); setNewPrice(''); }}>
                            ✕
                          </Button>
                        </>
                      ) : (
                        <>
                          <Badge className="bg-yellow-100 text-yellow-700 font-semibold">
                            {action.price} credite
                          </Badge>
                          {isSuperAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => { setEditingPrice(action.action_code); setNewPrice(action.price.toString()); }}>
                              <Edit3 className="w-4 h-4 text-slate-500" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tab Financiar (Non-SuperAdmin) — Proforme + Facturi de la e-Tesa */}
          {!isSuperAdmin && (
            <TabsContent value="financial" className="space-y-3">
              {financialStatus.credits_blocked && financialStatus.blocking_proforma && (
                <div className="rounded-lg border-2 border-red-500 bg-gradient-to-r from-red-50 to-orange-50 p-4 flex items-start gap-3 shadow-md" data-testid="credits-blocked-banner">
                  <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0 animate-pulse" />
                  <div className="flex-1">
                    <p className="font-bold text-red-900 text-sm sm:text-base">
                      ⚠️ Sistem de credite SUSPENDAT
                    </p>
                    <p className="text-xs sm:text-sm text-red-800 mt-1">
                      Proforma <strong>{financialStatus.blocking_proforma.series}{String(financialStatus.blocking_proforma.number).padStart(4,'0')}</strong> ({Number(financialStatus.blocking_proforma.total).toFixed(2)} RON) este expirată și neachitată. Toate acțiunile care consumă credite sunt blocate până la confirmarea plății.
                    </p>
                  </div>
                </div>
              )}
              {financialStatus.pending_proformas?.length > 0 && !financialStatus.credits_blocked && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-amber-900">
                    Ai <strong>{financialStatus.pending_proformas.length} proform{financialStatus.pending_proformas.length === 1 ? 'ă' : 'e'}</strong> de achitat. Asigură-te că plătești în 24h ca să eviți suspendarea creditelor.
                  </p>
                </div>
              )}
              <ScrollArea className="h-[420px]">
                {financialDocs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CircleDollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Nu ai documente financiare emise de e-Tesa</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {financialDocs.map((d) => {
                      const isProforma = d.invoice_doc_type === 'proforma';
                      const isAchitata = d.payment_status === 'achitata';
                      const isPending = d.payment_status === 'proforma_pending';
                      const docNo = `${d.series}${String(d.number).padStart(4,'0')}`;
                      const statusCfg = isAchitata
                        ? { color: 'bg-green-100 text-green-800 border-green-300', label: '✓ ACHITATĂ' }
                        : isPending
                          ? { color: 'bg-amber-100 text-amber-800 border-amber-300', label: '⏰ DE ACHITAT' }
                          : { color: 'bg-slate-100 text-slate-700 border-slate-300', label: d.payment_status || d.status };
                      return (
                        <div key={d.id} className={`p-3 border-2 rounded-lg ${isProforma ? 'border-amber-200 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/30'}`} data-testid={`fin-doc-${d.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-[180px]">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={isProforma ? 'bg-amber-600 text-white' : 'bg-emerald-600 text-white'}>
                                  {isProforma ? 'PROFORMĂ' : 'FACTURĂ FISCALĂ'}
                                </Badge>
                                <span className="font-bold text-slate-800">{docNo}</span>
                              </div>
                              <p className="text-lg font-bold mt-1">{Number(d.total || 0).toFixed(2)} <span className="text-xs font-normal text-slate-500">RON</span></p>
                              <p className="text-[10px] sm:text-xs text-slate-500">
                                Emisă: {d.invoice_date}
                                {isProforma && d.proforma_due_at && ` | Scadență: ${d.proforma_due_at.slice(0,16).replace('T',' ')}`}
                              </p>
                              <p className="text-[10px] sm:text-xs text-slate-600 mt-1">{d.notes}</p>
                            </div>
                            <div className="flex flex-col gap-2 items-end">
                              <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${statusCfg.color}`}>{statusCfg.label}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs"
                                onClick={() => setPreviewDoc(d)}
                                data-testid={`view-invoice-${d.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" /> Vizualizează {isProforma ? 'proforma' : 'factura'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          )}

          {/* Tab Cererile Mele (Non-SuperAdmin) */}
          {!isSuperAdmin && (
            <TabsContent value="my-requests">
              <ScrollArea className="h-[400px]">
                {myRequests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Nu ai trimis nicio cerere de credite</p>
                    <Button className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowRequestModal(true)}>
                      <Send className="w-4 h-4 mr-2" /> Trimite o cerere
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {myRequests.map((r) => (
                      <div key={r.id} className="p-3 border rounded-lg" data-testid={`my-request-${r.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-lg">{r.amount_requested.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} credite</p>
                            <p className="text-xs text-slate-500">{formatDate(r.created_at)}</p>
                            {r.advance_granted > 0 && (
                              <p className="text-xs text-emerald-700 mt-1">✓ Avans {r.advance_granted} credite acordat instant</p>
                            )}
                            {r.notes && <p className="text-xs text-slate-600 mt-1 italic">„{r.notes}"</p>}
                            {r.reject_reason && <p className="text-xs text-red-600 mt-1">Motiv respingere: {r.reject_reason}</p>}
                          </div>
                          {requestStatusBadge(r.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          )}

          {/* Tab Cereri Credite (SuperAdmin) */}
          {isSuperAdmin && (
            <TabsContent value="all-requests">
              <ScrollArea className="h-[450px]">
                {loadingRequests ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : allRequests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Nu există cereri de credite</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {allRequests.map((r) => (
                      <div key={r.id} className="p-3 border rounded-lg hover:bg-slate-50" data-testid={`request-${r.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Building2 className="w-4 h-4 text-slate-500" />
                              <p className="font-semibold">{r.company_name}</p>
                              <span className="text-xs text-slate-400">CUI: {r.company_cui || '—'}</span>
                            </div>
                            <p className="text-lg font-bold text-emerald-700 mt-1">{r.amount_requested.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} credite</p>
                            <p className="text-xs text-slate-500">{formatDate(r.created_at)}</p>
                            {r.advance_granted > 0 && (
                              <p className="text-xs text-emerald-700">Avans deja acordat: {r.advance_granted} credite</p>
                            )}
                            {r.notes && <p className="text-xs text-slate-600 mt-1 italic">„{r.notes}"</p>}
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            {requestStatusBadge(r.status)}
                            <div className="flex gap-2">
                              {r.status === 'pending' && (
                                <>
                                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleApproveRequest(r.id)} data-testid={`approve-${r.id}`}>
                                    <CheckCircle className="w-3 h-3 mr-1" /> Aprobă (emite factură)
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => handleRejectRequest(r.id)} data-testid={`reject-${r.id}`}>
                                    <XCircle className="w-3 h-3 mr-1" /> Respinge
                                  </Button>
                                </>
                              )}
                              {r.status === 'approved' && (
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleMarkPaid(r.id)} data-testid={`paid-${r.id}`}>
                                  <CircleDollarSign className="w-3 h-3 mr-1" /> Confirmă plata & alocă credite
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          )}

          {/* Tab Atribuire Credite (Super Admin) */}
          {isSuperAdmin && (
            <TabsContent value="companies">
              <ScrollArea className="h-[400px]">
                {loadingCompanies ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {companiesCredits.map((company) => (
                      <div key={company.company_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div>
                          <p className="font-medium">{company.company_name}</p>
                          <p className="text-xs text-slate-500">ID: {company.company_id}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-yellow-100 text-yellow-700">
                            {company.balance.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cr
                          </Badge>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedCompanyForAdd(company);
                              setShowAddCredits(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Atribuie
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>

      {/* Dialog Vizualizare Factură / Proformă (cu Plata Online) */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              {previewDoc?.invoice_doc_type === 'proforma' ? 'PROFORMĂ' : 'FACTURĂ FISCALĂ'} {previewDoc ? `${previewDoc.series}${String(previewDoc.number).padStart(4,'0')}` : ''}
            </DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-4">
              {/* Document Preview */}
              <div className="border-2 rounded-lg p-4 sm:p-6 bg-white print:shadow-none relative overflow-hidden" id="invoice-preview-content">
                {/* Banner DEMO */}
                <div className="bg-amber-50 border-2 border-amber-500 text-amber-800 rounded-md px-3 py-2 text-xs sm:text-sm font-semibold text-center mb-3">
                  ⚠ DOCUMENT DEMO — NEFISCAL · Platforma este în perioada de probe. Plata online este SIMULATĂ. <span className="font-bold">NU trimite acest document în contabilitate.</span>
                </div>
                {/* Watermark diagonal */}
                <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center select-none" style={{ zIndex: 0 }}>
                  <span className="text-red-500/10 font-black tracking-widest" style={{ fontSize: '88px', transform: 'rotate(-28deg)' }}>DEMO · NEFISCAL</span>
                </div>
                <div className="relative" style={{ zIndex: 1 }}>
                <div className="flex justify-between items-start border-b-2 border-teal-600 pb-3 mb-4">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-teal-700">
                      {previewDoc.invoice_doc_type === 'proforma' ? 'PROFORMĂ (DEMO)' : 'FACTURĂ (DEMO)'}
                    </h2>
                    <p className="text-sm">Seria <strong>{previewDoc.series}</strong> Nr. <strong>{String(previewDoc.number).padStart(4,'0')}</strong></p>
                    <p className="text-xs text-slate-500">Data: {previewDoc.invoice_date}</p>
                    {previewDoc.proforma_due_at && (
                      <p className="text-xs text-red-600 font-semibold">Scadență: {previewDoc.proforma_due_at.slice(0,16).replace('T',' ')}</p>
                    )}
                  </div>
                  <Badge className={previewDoc.payment_status === 'achitata' ? 'bg-green-600' : 'bg-amber-600'}>
                    {previewDoc.payment_status === 'achitata' ? '✓ ACHITATĂ' : '⏰ DE ACHITAT'}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="font-bold text-xs text-slate-500 uppercase mb-1">Furnizor</p>
                    <p className="font-semibold">{previewDoc.supplier_name}</p>
                    <p className="text-xs">CUI: {previewDoc.supplier_cui}</p>
                    <p className="text-xs text-slate-600">{previewDoc.supplier_address}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="font-bold text-xs text-slate-500 uppercase mb-1">Client</p>
                    <p className="font-semibold">{previewDoc.client_name}</p>
                    <p className="text-xs">CUI: {previewDoc.client_cui || '—'}</p>
                    <p className="text-xs text-slate-600">{previewDoc.client_address || ''}</p>
                  </div>
                </div>

                <table className="w-full border-collapse text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-teal-600 text-white">
                      <th className="p-2 text-left border">Denumire</th>
                      <th className="p-2 text-center border w-12">UM</th>
                      <th className="p-2 text-right border w-16">Cant.</th>
                      <th className="p-2 text-right border w-24">Preț unit.</th>
                      <th className="p-2 text-right border w-16">TVA</th>
                      <th className="p-2 text-right border w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewDoc.items || []).map((it, i) => (
                      <tr key={i}>
                        <td className="p-2 border">{it.name}</td>
                        <td className="p-2 text-center border">{it.um}</td>
                        <td className="p-2 text-right border">{it.quantity}</td>
                        <td className="p-2 text-right border">{Number(it.unit_price).toFixed(2)}</td>
                        <td className="p-2 text-right border">{Number(it.vat_percent).toFixed(0)}%</td>
                        <td className="p-2 text-right border font-semibold">{Number(it.total).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="text-right mt-3 space-y-1 text-sm">
                  <p>Subtotal: <strong>{Number(previewDoc.subtotal || 0).toFixed(2)} RON</strong></p>
                  <p>TVA: <strong>{Number(previewDoc.vat_amount || 0).toFixed(2)} RON</strong></p>
                  <p className="text-lg sm:text-xl text-red-600">TOTAL: <strong>{Number(previewDoc.total || 0).toFixed(2)} RON</strong></p>
                </div>
                <p className="text-[10px] text-slate-500 italic mt-3 text-center">
                  {previewDoc.notes || ''}
                </p>
                <div className="mt-3 bg-amber-50 border-l-4 border-amber-500 text-amber-900 rounded-r p-3 text-[11px] leading-relaxed">
                  <strong>ATENȚIE:</strong> Acest document este generat în <strong>mod DEMO</strong> pentru testarea funcționalităților platformei e-Tesa.
                  Plata online este simulată — nu există tranzacție bancară reală.
                  Acest document <strong>nu poate fi folosit în scopuri contabile, fiscale sau juridice</strong>.
                  Documentele fiscale reale vor fi emise după activarea integrării de plată cu Stripe/Netopia.
                </div>
                <p className="text-[10px] text-slate-400 text-center mt-2">
                  Document generat prin <a href="https://e-tesa.ro" className="text-teal-600 underline">e-Tesa.ro</a>
                </p>
                </div>
              </div>

              {/* Plata Online Button (doar pentru proforma neachitată) */}
              {previewDoc.invoice_doc_type === 'proforma' && previewDoc.payment_status !== 'achitata' && (
                <div className="flex flex-col sm:flex-row gap-2 justify-end pt-3 border-t">
                  <Button variant="outline" onClick={() => setPreviewDoc(null)}>
                    Închide
                  </Button>
                  <Button
                    onClick={() => handlePayOnline(previewDoc.id)}
                    disabled={payingOnline}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                    data-testid="pay-online-btn"
                  >
                    {payingOnline ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    {payingOnline ? 'Procesare...' : `Plată online (DEMO) ${Number(previewDoc.total).toFixed(2)} RON`}
                  </Button>
                </div>
              )}
              {previewDoc.payment_status === 'achitata' && (
                <div className="flex justify-end pt-3 border-t">
                  <Button variant="outline" onClick={() => setPreviewDoc(null)}>Închide</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Solicită Credite (Non-SuperAdmin) */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-600" />
              Solicită Credite
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs sm:text-sm text-emerald-800 space-y-1">
              <p>✅ <strong>Creditele se alocă INSTANT</strong> în contul tău pentru a-ți continua activitatea.</p>
              <p>📄 Vei primi o <strong>proformă pe email</strong> de la firma proprietară (CUI 44303561).</p>
              <p>⏰ <strong className="text-red-600">Achit-o în 24 ore</strong> — altfel sistemul de credite se va suspenda automat până la confirmarea plății.</p>
              <p>✓ După plată, primești <strong>factura fiscală cu status ACHITATĂ</strong>.</p>
            </div>
            <div>
              <Label>Câte credite vrei? <span className="text-slate-400">(1 credit = 1 RON cu TVA inclus)</span></Label>
              <Input
                type="number"
                min="1"
                max="500"
                value={requestAmount}
                onChange={(e) => setRequestAmount(e.target.value)}
                placeholder="Ex: 200"
                className="text-lg"
                data-testid="request-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
                <span>
                  Plafon temporar: <strong>maxim 500 credite</strong> per cerere, iar soldul total nu poate depăși 500.
                  Sold curent: <strong>{balance}</strong> · Mai poți cere maxim <strong>{Math.max(0, 500 - Math.floor(balance))}</strong> credite.
                </span>
              </p>
            </div>
            <div>
              <Label>Note (opțional)</Label>
              <Input
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                placeholder="Ex: am nevoie pentru luna în curs"
                data-testid="request-notes-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRequestModal(false)} className="flex-1">
                Anulează
              </Button>
              <Button onClick={handleSubmitRequest} disabled={submittingRequest} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-request-btn">
                {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Trimite cererea
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Adăugare Credite */}
      <Dialog open={showAddCredits} onOpenChange={setShowAddCredits}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              {selectedCompanyForAdd ? `Atribuie Credite - ${selectedCompanyForAdd.company_name}` : 'Adaugă Credite'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!isSuperAdmin && !selectedCompanyForAdd && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Limită: maxim 500 credite (sold curent: {balance})</span>
              </div>
            )}
            
            <div>
              <Label>Suma (credite)</Label>
              <Input 
                type="number"
                value={addAmount}
                onChange={(e) => setAddAmount(e.target.value)}
                placeholder="Ex: 100"
                className="text-lg"
              />
            </div>
            
            <div>
              <Label>Descriere</Label>
              <Input 
                value={addDescription}
                onChange={(e) => setAddDescription(e.target.value)}
                placeholder="Alimentare cont"
              />
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowAddCredits(false); setSelectedCompanyForAdd(null); }} className="flex-1">
                Anulează
              </Button>
              <Button onClick={handleAddCredits} disabled={addingCredits} className="flex-1 bg-green-600 hover:bg-green-700">
                {addingCredits ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Adaugă
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
